import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    LuWrench,
    LuZap,
    LuSlidersHorizontal,
    LuSave,
    LuX,
    LuPenLine,
    LuPalette,
    LuLayoutDashboard,
    LuChevronUp,
    LuChevronDown,
    LuChevronLeft,
    LuChevronRight,
    LuBookOpen,
    LuShoppingCart,
    LuPackage,
    LuCalendar,
    LuFileText,
    LuPlus,
    LuFilePlus,
    LuSparkles,
    LuUpload,
    LuFileCode,
    LuTrash2,
    LuCircleAlert,
    LuFolderOpen,
    LuDownload,
    LuAlignLeft,
    LuAlignCenter,
    LuAlignRight
} from 'react-icons/lu';
import { normalizeSlug } from '../utils/helpers.js';
import { getApiUrl, authHeaders, resolveMediaUrl, safePost } from '../utils/styleHelpers.js';
import { validateAndParseTemplate, processImportedPage } from '../utils/templateHelpers.js';
import { MediaLibraryModal } from './MediaLibraryModal.jsx';
import axios from 'axios';

/**
 * Helper to convert legacy emoji badges into clean, single-color Lucide icons.
 */
const renderBadgeIcon = (icon) => {
    if (!icon || icon === '🛠️' || icon === '⚙️') return <LuWrench size={14} style={{ flexShrink: 0 }} />;
    if (icon === '📖') return <LuBookOpen size={14} style={{ flexShrink: 0 }} />;
    if (icon === '🛍️' || icon === '🛒') return <LuShoppingCart size={14} style={{ flexShrink: 0 }} />;
    if (icon === '📦') return <LuPackage size={14} style={{ flexShrink: 0 }} />;
    if (icon === '📅') return <LuCalendar size={14} style={{ flexShrink: 0 }} />;
    if (icon === '📰' || icon === '📝') return <LuFileText size={14} style={{ flexShrink: 0 }} />;
    if (React.isValidElement(icon)) return icon;
    return <LuWrench size={14} style={{ flexShrink: 0 }} />;
};

/**
 * FloatingAdminBar — Reusable, fully responsive floating bottom editor toolbar
 * Optimized for iOS Safari, iPadOS, Android, and Desktop with full touch pan-x,
 * hardware compositing, pointer drag scrolling, and overflow indicators.
 */
export const FloatingAdminBar = ({
    title = 'Page Editor',
    badgeIcon = '🛠️',
    isEditing = false,
    onEdit,
    onSave,
    onCancel,
    saving = false,
    onCustomize,
    customizerLabel = 'Customize',
    customActions,
    dashboardUrl = '/dashboard',
    storageKey = 'omnivaso_admin_bar_minimized',
    extraButtons,
    editorMode,
    onModeChange,
    showModeToggle = true,
    config,
    onSaveConfig,
    onCreatePage,
    showCreatePage = true,
    onExport,
    position: externalPosition,
    defaultPosition = 'center',
    onPositionChange
}) => {
    const navigate = useNavigate();
    const [isNewPageModalOpen, setIsNewPageModalOpen] = useState(false);
    const [newPageTitle, setNewPageTitle] = useState('');
    const [newPageSlug, setNewPageSlug] = useState('');
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
    const [newPageTemplate, setNewPageTemplate] = useState('blank');
    const [isCreatingPage, setIsCreatingPage] = useState(false);
    const [customTemplateJson, setCustomTemplateJson] = useState(null);
    const [customTemplateFileName, setCustomTemplateFileName] = useState('');
    const [templateUploadError, setTemplateUploadError] = useState('');
    const [isMediaModalOpenForTemplate, setIsMediaModalOpenForTemplate] = useState(false);
    const fileInputRef = useRef(null);

    const handleTemplateFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setTemplateUploadError('');

        if (!file.name.toLowerCase().endsWith('.json')) {
            setTemplateUploadError('Please select a valid .json template file.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const text = event.target.result;
                const validation = validateAndParseTemplate(text);
                if (!validation.valid) {
                    setTemplateUploadError(validation.error || 'Invalid template format. The JSON file must contain a "sections" array or "hero" configuration.');
                    return;
                }

                const pageData = validation.page;
                setCustomTemplateJson(pageData);
                setCustomTemplateFileName(file.name);
                setNewPageTemplate('custom');
                setTemplateUploadError('');

                // Auto-fill title and slug if not manually specified yet
                if (!newPageTitle.trim() && validation.stats?.title && validation.stats.title !== 'Untitled Page') {
                    setNewPageTitle(validation.stats.title);
                    if (!isSlugManuallyEdited && validation.stats.slug) {
                        setNewPageSlug(normalizeSlug(validation.stats.slug));
                    }
                }
            } catch (err) {
                setTemplateUploadError('Failed to parse JSON file. Please ensure the file is valid JSON.');
            }
        };
        reader.readAsText(file);
    };

    const handleSelectTemplateFromUploads = async (url, item) => {
        try {
            setTemplateUploadError('');
            const fullUrl = resolveMediaUrl(url);
            const res = await fetch(fullUrl);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const text = await res.text();
            
            const validation = validateAndParseTemplate(text);
            if (!validation.valid) {
                setTemplateUploadError(validation.error || 'Invalid template JSON structure in selected file.');
                return;
            }

            const pageData = validation.page;
            setCustomTemplateJson(pageData);
            setCustomTemplateFileName(item?.name ? `${item.name} (From Uploads)` : 'Template from Uploads');
            setNewPageTemplate('custom');
            setTemplateUploadError('');

            // Auto-fill title and slug if not manually specified yet
            if (!newPageTitle.trim() && validation.stats?.title && validation.stats.title !== 'Untitled Page') {
                setNewPageTitle(validation.stats.title);
                if (!isSlugManuallyEdited && validation.stats.slug) {
                    setNewPageSlug(normalizeSlug(validation.stats.slug));
                }
            }
        } catch (err) {
            console.error('Error loading template from uploads:', err);
            setTemplateUploadError('Failed to load template file from server: ' + err.message);
        }
    };

    const handleClearCustomTemplate = () => {
        setCustomTemplateJson(null);
        setCustomTemplateFileName('');
        setTemplateUploadError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setNewPageTemplate('blank');
    };

    const [isMinimized, setIsMinimized] = useState(() => {
        try {
            if (storageKey) {
                return localStorage.getItem(storageKey) === 'true';
            }
        } catch (e) {}
        return false;
    });

    const [internalMode, setInternalMode] = useState(() => {
        try {
            return localStorage.getItem('omnivaso_editor_mode') || 'simple';
        } catch (e) {
            return 'simple';
        }
    });

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const [barPosition, setBarPosition] = useState(() => {
        if (externalPosition && ['left', 'center', 'right'].includes(externalPosition)) {
            return externalPosition;
        }
        try {
            const saved = localStorage.getItem('omnivaso_admin_bar_position');
            if (saved && ['left', 'center', 'right'].includes(saved)) {
                return saved;
            }
        } catch (e) {}
        return defaultPosition || 'center';
    });

    useEffect(() => {
        if (externalPosition && ['left', 'center', 'right'].includes(externalPosition)) {
            setBarPosition(externalPosition);
        }
    }, [externalPosition]);

    const handlePositionChange = (newPos) => {
        if (!['left', 'center', 'right'].includes(newPos)) return;
        setBarPosition(newPos);
        try {
            localStorage.setItem('omnivaso_admin_bar_position', newPos);
        } catch (e) {}
        if (onPositionChange) {
            onPositionChange(newPos);
        }
    };

    const handleCyclePosition = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        const order = ['left', 'center', 'right'];
        const nextIdx = (order.indexOf(barPosition) + 1) % order.length;
        handlePositionChange(order[nextIdx]);
    };

    const scrollRef = useRef(null);
    const isPointerDownRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const hasDraggedRef = useRef(false);

    const currentMode = editorMode !== undefined ? editorMode : internalMode;

    const checkScrollOverflow = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanScrollLeft(scrollLeft > 4);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 4);
    }, []);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = 0;
        }
        checkScrollOverflow();
        const el = scrollRef.current;
        if (!el) return;

        const onResize = () => checkScrollOverflow();
        window.addEventListener('resize', onResize, { passive: true });
        return () => window.removeEventListener('resize', onResize);
    }, [checkScrollOverflow, isEditing, isMinimized, customActions, extraButtons]);

    const handleModeSwitch = (mode) => {
        if (editorMode === undefined) {
            setInternalMode(mode);
        }
        try {
            localStorage.setItem('omnivaso_editor_mode', mode);
        } catch (e) {}
        if (onModeChange) {
            onModeChange(mode);
        }
    };

    const handleToggleMinimize = (val) => {
        const next = typeof val === 'boolean' ? val : !isMinimized;
        setIsMinimized(next);
        try {
            if (storageKey) {
                localStorage.setItem(storageKey, String(next));
            }
        } catch (e) {}
    };

    // Horizontal wheel translation for mice/trackpads
    const handleWheel = (e) => {
        const el = scrollRef.current;
        if (!el) return;
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            el.scrollLeft += e.deltaY;
            checkScrollOverflow();
        }
    };

    // Pointer-drag scroll handlers for universal swipe/drag
    const handlePointerDown = (e) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        const el = scrollRef.current;
        if (!el) return;
        isPointerDownRef.current = true;
        startXRef.current = e.pageX - el.offsetLeft;
        scrollLeftRef.current = el.scrollLeft;
        hasDraggedRef.current = false;
    };

    const handlePointerMove = (e) => {
        if (!isPointerDownRef.current) return;
        const el = scrollRef.current;
        if (!el) return;
        const x = e.pageX - el.offsetLeft;
        const walk = x - startXRef.current;
        if (Math.abs(walk) > 6) {
            if (!hasDraggedRef.current) {
                hasDraggedRef.current = true;
                setIsDragging(true);
            }
            el.scrollLeft = scrollLeftRef.current - walk;
            checkScrollOverflow();
        }
    };

    const handlePointerUp = () => {
        isPointerDownRef.current = false;
        setTimeout(() => {
            hasDraggedRef.current = false;
            setIsDragging(false);
        }, 50);
    };

    const scrollByAmount = (amount) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: amount, behavior: 'smooth' });
    };

    const handleTitleChange = (val) => {
        setNewPageTitle(val);
        if (!isSlugManuallyEdited) {
            setNewPageSlug(normalizeSlug(val));
        }
    };

    const handleCreatePageSubmit = async (e) => {
        e.preventDefault();
        if (!newPageTitle.trim()) {
            alert('Please enter a page title.');
            return;
        }
        const cleanSlug = normalizeSlug(newPageSlug || newPageTitle);
        if (!cleanSlug) {
            alert('Please enter a valid URL slug for the page.');
            return;
        }

        const reserved = ['admin', 'login', 'articles', 'shop', 'cart', 'search', 'product', 'why-omnivaso', 'comparison'];
        if (reserved.includes(cleanSlug)) {
            alert(`The slug "/${cleanSlug}" is reserved by the system. Please choose a different slug.`);
            return;
        }

        const currentPages = config?.pages || [];
        if (currentPages.some(p => normalizeSlug(p.slug) === cleanSlug)) {
            alert(`A page with URL slug "/${cleanSlug}" already exists. Please choose a unique slug.`);
            return;
        }

        setIsCreatingPage(true);
        const timestamp = Date.now();

        let templateSections = [
            {
                id: `sec-${timestamp}-1`,
                type: 'columns',
                title: 'Welcome Section',
                heading: `Welcome to ${newPageTitle.trim()}`,
                subHeading: 'Click "Edit Page" below to customize this section, add columns, images, or change styling!',
                backgroundColor: '#ffffff',
                padding: '4rem 2rem',
                columns: []
            }
        ];

        let customHero = null;

        if (newPageTemplate === 'services') {
            templateSections = [
                {
                    id: `sec-${timestamp}-1`,
                    type: 'columns',
                    title: 'Our Key Offerings',
                    heading: 'What We Offer',
                    subHeading: 'Discover our premium features and services designed for excellence.',
                    backgroundColor: '#ffffff',
                    padding: '4rem 2rem',
                    columns: [
                        { id: `col-${timestamp}-1`, title: 'Feature One', text: 'Detailed description of feature or service one.', icon: '⚡', width: '33%' },
                        { id: `col-${timestamp}-2`, title: 'Feature Two', text: 'Detailed description of feature or service two.', icon: '🌟', width: '33%' },
                        { id: `col-${timestamp}-3`, title: 'Feature Three', text: 'Detailed description of feature or service three.', icon: '🚀', width: '33%' }
                    ]
                }
            ];
        } else if (newPageTemplate === 'contact') {
            templateSections = [
                {
                    id: `sec-${timestamp}-1`,
                    type: 'contact',
                    title: 'Get In Touch',
                    heading: 'Contact Us',
                    subHeading: 'Have questions or want to learn more? Send us a message below.',
                    backgroundColor: '#f8fafc',
                    padding: '4rem 2rem'
                }
            ];
        } else if (newPageTemplate === 'gallery') {
            templateSections = [
                {
                    id: `sec-${timestamp}-1`,
                    type: 'gallery',
                    title: 'Photo Gallery',
                    heading: 'Media Showcase',
                    subHeading: 'Explore our latest project images and highlights.',
                    backgroundColor: '#ffffff',
                    padding: '4rem 2rem',
                    images: [
                        { id: `img-1`, url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80', caption: 'Highlight Event' },
                        { id: `img-2`, url: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&q=80', caption: 'Community Gathering' }
                    ]
                }
            ];
        } else if (newPageTemplate === 'custom' && customTemplateJson) {
            const processed = processImportedPage(
                customTemplateJson,
                newPageTitle.trim(),
                cleanSlug,
                currentPages
            );
            templateSections = processed.sections || [];
            customHero = processed.hero || null;
        }

        const newPageObj = {
            id: timestamp,
            title: newPageTitle.trim(),
            slug: cleanSlug,
            image: customTemplateJson?.image || '',
            content: customTemplateJson?.content || '',
            hero: customHero || {
                title: newPageTitle.trim(),
                subtitle: 'Customize your new page hero title and subtitle in Live Edit mode.',
                buttons: [],
                backgroundColor: '#0f172a',
                textColor: '#ffffff'
            },
            sections: templateSections
        };

        const updatedConfig = {
            ...(config || {}),
            pages: [...currentPages, newPageObj]
        };

        if (typeof onCreatePage === 'function') {
            onCreatePage(newPageObj, updatedConfig);
        } else if (typeof onSaveConfig === 'function') {
            onSaveConfig(updatedConfig);
        } else {
            try {
                const targetUrl = getApiUrl('api.php');
                await safePost(targetUrl, updatedConfig, authHeaders({ headers: { 'Content-Type': 'application/json' } }));
            } catch (err) {}
            try {
                localStorage.setItem('omnivaso_cached_config', JSON.stringify(updatedConfig));
            } catch (e) {}
        }

        setIsCreatingPage(false);
        setIsNewPageModalOpen(false);
        setNewPageTitle('');
        setNewPageSlug('');
        setIsSlugManuallyEdited(false);
        setCustomTemplateJson(null);
        setCustomTemplateFileName('');
        setTemplateUploadError('');

        try {
            localStorage.setItem('omnivaso_live_edit_auto', 'true');
        } catch (e) {}

        navigate(`/${cleanSlug}`);
    };

    const handleSaveClick = async (e) => {
        // Dispatch global and module save events so all modules commit pending states before persist
        if (typeof window !== 'undefined') {
            try {
                window.dispatchEvent(new CustomEvent('dashboard-save-all', { detail: { timestamp: Date.now() } }));
                window.dispatchEvent(new CustomEvent('modules-save-all', { detail: { timestamp: Date.now() } }));
                window.dispatchEvent(new CustomEvent('page-save-triggered', { detail: { timestamp: Date.now() } }));
            } catch (err) {
                console.warn('Could not dispatch save events from FloatingAdminBar:', err);
            }
        }

        if (typeof onSave === 'function') {
            await onSave(e);
        } else if (typeof onSaveConfig === 'function') {
            await onSaveConfig(config);
        }
    };

    return (
        <>
            <style>{`
                .floating-admin-bar-wrap {
                    position: fixed;
                    bottom: calc(14px + env(safe-area-inset-bottom, 0px));
                    isolation: isolate;
                    z-index: 99999;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 6px;
                    background: rgba(15, 23, 42, 0.95);
                    backdrop-filter: blur(16px);
                    -webkit-backdrop-filter: blur(16px);
                    border: 1px solid rgba(255, 255, 255, 0.18);
                    box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.08);
                    border-radius: 40px;
                    padding: 6px 10px;
                    color: white;
                    width: max-content;
                    max-width: min(calc(100vw - 16px), calc(100% - 16px));
                    box-sizing: border-box;
                    transition: left 0.3s cubic-bezier(0.16, 1, 0.3, 1), right 0.3s cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), bottom 0.25s ease, background 0.2s ease;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
                    touch-action: pan-x;
                    user-select: none;
                    -webkit-user-select: none;
                }
                .floating-admin-bar-wrap.position-center {
                    left: 50%;
                    right: auto;
                    -webkit-transform: translate3d(-50%, 0, 0);
                    transform: translate3d(-50%, 0, 0);
                }
                .floating-admin-bar-wrap.position-left {
                    left: calc(18px + env(safe-area-inset-left, 0px));
                    right: auto;
                    -webkit-transform: translate3d(0, 0, 0);
                    transform: translate3d(0, 0, 0);
                }
                .floating-admin-bar-wrap.position-right {
                    left: auto;
                    right: calc(18px + env(safe-area-inset-right, 0px));
                    -webkit-transform: translate3d(0, 0, 0);
                    transform: translate3d(0, 0, 0);
                }
                .floating-admin-pos-control {
                    display: inline-flex;
                    align-items: center;
                    background: rgba(0, 0, 0, 0.35);
                    border: 1px solid rgba(255, 255, 255, 0.14);
                    border-radius: 20px;
                    padding: 2px;
                    gap: 1px;
                    flex-shrink: 0;
                }
                .floating-admin-pos-btn {
                    border: none;
                    background: transparent;
                    color: rgba(255, 255, 255, 0.65);
                    border-radius: 14px;
                    width: 22px;
                    height: 22px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    padding: 0;
                    transition: all 0.15s ease;
                }
                .floating-admin-pos-btn:hover {
                    color: #ffffff;
                    background: rgba(255, 255, 255, 0.15);
                }
                .floating-admin-pos-btn.active {
                    background: #3b82f6;
                    color: #ffffff;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
                }
                .floating-admin-bar-scroll {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    overflow-x: auto;
                    overflow-y: hidden;
                    flex: 1 1 auto;
                    min-width: 0;
                    width: 100%;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                    -webkit-overflow-scrolling: touch;
                    overscroll-behavior-x: contain;
                    overscroll-behavior-y: none;
                    touch-action: pan-x;
                    padding: 2px 2px;
                    position: relative;
                    cursor: grab;
                }
                .floating-admin-bar-scroll.is-dragging {
                    cursor: grabbing;
                    user-select: none;
                    -webkit-user-select: none;
                }
                .floating-admin-bar-scroll::-webkit-scrollbar {
                    display: none;
                    width: 0;
                    height: 0;
                }
                .floating-admin-btn {
                    flex-shrink: 0;
                    white-space: nowrap;
                    font-size: 0.8rem;
                    font-weight: 600;
                    border: none;
                    border-radius: 20px;
                    padding: 6px 12px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    text-decoration: none;
                    transition: transform 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
                    line-height: 1.2;
                    touch-action: pan-x;
                    -webkit-tap-highlight-color: transparent;
                    user-select: none;
                    -webkit-user-select: none;
                }
                .floating-admin-btn:hover {
                    transform: translateY(-1px);
                }
                .floating-admin-btn:active {
                    transform: translateY(1px);
                }
                .floating-admin-btn-primary {
                    background: #3b82f6;
                    color: #ffffff;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.35);
                }
                .floating-admin-btn-primary:hover {
                    background: #2563eb;
                }
                .floating-admin-btn-save {
                    background: #10b981;
                    color: #ffffff;
                    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
                }
                .floating-admin-btn-save:hover {
                    background: #059669;
                }
                .floating-admin-btn-danger {
                    background: rgba(239, 68, 68, 0.2);
                    border: 1px solid rgba(239, 68, 68, 0.4) !important;
                    color: #fca5a5;
                }
                .floating-admin-btn-danger:hover {
                    background: #ef4444;
                    color: #ffffff;
                }
                .floating-admin-btn-customizer {
                    background: #8b5cf6;
                    color: #ffffff;
                    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.35);
                }
                .floating-admin-btn-customizer:hover {
                    background: #7c3aed;
                }
                .floating-admin-btn-ghost {
                    background: rgba(255, 255, 255, 0.1);
                    color: #e2e8f0;
                    border: 1px solid rgba(255, 255, 255, 0.1) !important;
                }
                .floating-admin-btn-ghost:hover {
                    background: rgba(255, 255, 255, 0.2);
                    color: #ffffff;
                }
                .floating-admin-mode-toggle {
                    display: inline-flex;
                    align-items: center;
                    background: rgba(0, 0, 0, 0.45);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    border-radius: 20px;
                    padding: 2px;
                    gap: 2px;
                    flex-shrink: 0;
                    touch-action: pan-x;
                }
                .floating-admin-mode-btn {
                    border: none;
                    background: transparent;
                    color: #94a3b8;
                    font-size: 0.72rem;
                    font-weight: 600;
                    padding: 3px 8px;
                    border-radius: 14px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 3px;
                    transition: all 0.15s ease;
                    white-space: nowrap;
                    line-height: 1.2;
                    touch-action: pan-x;
                    -webkit-tap-highlight-color: transparent;
                }
                .floating-admin-mode-btn:hover {
                    color: #ffffff;
                }
                .floating-admin-mode-btn.active-simple {
                    background: #3b82f6;
                    color: #ffffff;
                    box-shadow: 0 1px 5px rgba(59, 130, 246, 0.4);
                }
                .floating-admin-mode-btn.active-advanced {
                    background: #8b5cf6;
                    color: #ffffff;
                    box-shadow: 0 1px 5px rgba(139, 92, 246, 0.4);
                }
                .floating-admin-min-btn {
                    flex-shrink: 0;
                    background: rgba(255, 255, 255, 0.12);
                    color: #94a3b8;
                    border: none;
                    width: 28px;
                    height: 28px;
                    min-width: 28px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.72rem;
                    transition: background 0.15s, color 0.15s;
                    margin-left: 2px;
                    touch-action: pan-x;
                    -webkit-tap-highlight-color: transparent;
                }
                .floating-admin-min-btn:hover {
                    background: rgba(255, 255, 255, 0.25);
                    color: white;
                }
                .floating-admin-nav-arrow {
                    flex-shrink: 0;
                    background: rgba(30, 41, 59, 0.85);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    color: #94a3b8;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.75rem;
                    cursor: pointer;
                    padding: 0;
                    touch-action: pan-x;
                    -webkit-tap-highlight-color: transparent;
                    transition: all 0.15s ease;
                }
                .floating-admin-nav-arrow:hover {
                    background: #3b82f6;
                    color: #ffffff;
                    border-color: #60a5fa;
                }
                .floating-admin-lead-group {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    flex-shrink: 0;
                }
                .floating-admin-primary-actions {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    flex-shrink: 0;
                }
                @media (max-width: 768px) {
                    .floating-admin-bar-wrap,
                    .floating-admin-bar-wrap.position-center,
                    .floating-admin-bar-wrap.position-left,
                    .floating-admin-bar-wrap.position-right {
                        left: 50% !important;
                        right: auto !important;
                        -webkit-transform: translate3d(-50%, 0, 0) !important;
                        transform: translate3d(-50%, 0, 0) !important;
                        bottom: calc(10px + env(safe-area-inset-bottom, 0px));
                        padding: 5px 8px;
                        gap: 5px;
                        border-radius: 28px;
                        max-width: calc(100vw - 12px) !important;
                        width: auto !important;
                    }
                    .floating-admin-pos-control {
                        display: none !important;
                    }
                    .floating-admin-title-text {
                        display: none !important;
                    }
                    .floating-admin-divider {
                        display: none !important;
                    }
                    .floating-admin-btn {
                        font-size: 0.74rem;
                        padding: 5px 9px;
                        gap: 4px;
                    }
                }
                @media (max-width: 480px) {
                    .floating-admin-bar-wrap,
                    .floating-admin-bar-wrap.position-center,
                    .floating-admin-bar-wrap.position-left,
                    .floating-admin-bar-wrap.position-right {
                        bottom: calc(8px + env(safe-area-inset-bottom, 0px));
                        padding: 4px 6px;
                        gap: 4px;
                        max-width: calc(100vw - 10px) !important;
                    }
                    .floating-admin-lead-group {
                        gap: 4px;
                    }
                    .floating-admin-primary-actions {
                        gap: 3px;
                    }
                    .floating-admin-btn {
                        font-size: 0.72rem;
                        padding: 5px 8px;
                        gap: 3px;
                    }
                }
            `}</style>

            <div className={`floating-admin-bar-wrap position-${barPosition}`}>
                {isMinimized ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <button
                            type="button"
                            onClick={() => handleToggleMinimize(false)}
                            className="floating-admin-btn floating-admin-btn-primary"
                            style={{ padding: '7px 14px', borderRadius: '24px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                            title={`Expand ${title} controls`}
                        >
                            {renderBadgeIcon(badgeIcon)}
                            <span>{title}</span>
                            <LuChevronUp size={13} style={{ opacity: 0.85, flexShrink: 0 }} />
                        </button>
                        <button
                            type="button"
                            onClick={handleCyclePosition}
                            className="floating-admin-nav-arrow"
                            style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                            title={`Toolbar docked ${barPosition.toUpperCase()}. Click to reposition to Left, Centre, or Right`}
                            aria-label="Reposition toolbar"
                        >
                            {barPosition === 'left' && <LuAlignLeft size={13} />}
                            {barPosition === 'center' && <LuAlignCenter size={13} />}
                            {barPosition === 'right' && <LuAlignRight size={13} />}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Title Badge & Always-Visible Primary Action (Edit Page when viewing; Save & Cancel when editing) */}
                        <div className="floating-admin-lead-group" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            <div className="floating-admin-title-badge" style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '4px', flexShrink: 0 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', color: '#93c5fd' }}>{renderBadgeIcon(badgeIcon)}</span>
                                <span className="floating-admin-title-text" style={{ fontSize: '0.8rem', fontWeight: '700', color: '#93c5fd', whiteSpace: 'nowrap' }}>{title}</span>
                            </div>

                            <div className="floating-admin-divider" style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />

                            <div className="floating-admin-primary-actions" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                {isEditing ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleSaveClick}
                                            disabled={saving}
                                            className="floating-admin-btn floating-admin-btn-save"
                                            title="Save all changes"
                                        >
                                            <LuSave size={13} style={{ flexShrink: 0 }} />
                                            <span>{saving ? 'Saving...' : 'Save'}</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onCancel}
                                            className="floating-admin-btn floating-admin-btn-danger"
                                            title="Discard changes and exit edit mode"
                                        >
                                            <LuX size={13} style={{ flexShrink: 0 }} />
                                            <span>Cancel</span>
                                        </button>
                                    </>
                                ) : (
                                    onEdit && (
                                        <button
                                            type="button"
                                            onClick={onEdit}
                                            className="floating-admin-btn floating-admin-btn-primary"
                                            title="Enter edit mode"
                                        >
                                            <LuPenLine size={13} style={{ flexShrink: 0 }} />
                                            <span>Edit Page</span>
                                        </button>
                                    )
                                )}
                            </div>
                        </div>

                        {/* Left Scroll Indicator / Quick Nudger */}
                        {canScrollLeft && (
                            <button
                                type="button"
                                onClick={() => scrollByAmount(-120)}
                                className="floating-admin-nav-arrow"
                                title="Scroll left"
                                aria-label="Scroll toolbar left"
                            >
                                <LuChevronLeft size={13} style={{ flexShrink: 0 }} />
                            </button>
                        )}

                        <div
                            ref={scrollRef}
                            className={`floating-admin-bar-scroll ${isDragging ? 'is-dragging' : ''}`}
                            onScroll={checkScrollOverflow}
                            onWheel={handleWheel}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp}
                        >
                            {/* Mode Toggle (When editing) */}
                            {isEditing && showModeToggle && (
                                <div className="floating-admin-mode-toggle" title="Switch editor simplicity mode">
                                    <button
                                        type="button"
                                        onClick={() => handleModeSwitch('simple')}
                                        className={`floating-admin-mode-btn ${currentMode === 'simple' ? 'active-simple' : ''}`}
                                    >
                                        <LuZap size={11} style={{ flexShrink: 0 }} />
                                        <span>Simple</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleModeSwitch('advanced')}
                                        className={`floating-admin-mode-btn ${currentMode === 'advanced' ? 'active-advanced' : ''}`}
                                    >
                                        <LuSlidersHorizontal size={11} style={{ flexShrink: 0 }} />
                                        <span>Advanced</span>
                                    </button>
                                </div>
                            )}

                            {customActions}

                            {showCreatePage && (
                                <button
                                    type="button"
                                    onClick={() => setIsNewPageModalOpen(true)}
                                    className="floating-admin-btn"
                                    title="Create a new page and start editing in real-time"
                                    style={{
                                        background: '#10b981',
                                        color: '#ffffff',
                                        border: 'none',
                                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                    }}
                                >
                                    <LuPlus size={13} style={{ flexShrink: 0 }} />
                                    <span>New Page</span>
                                </button>
                            )}

                            {onExport && (
                                <button
                                    type="button"
                                    onClick={onExport}
                                    className="floating-admin-btn floating-admin-btn-ghost"
                                    title="Download this page layout and sections as a reusable JSON template"
                                >
                                    <LuDownload size={13} style={{ flexShrink: 0 }} />
                                    <span>Export</span>
                                </button>
                            )}

                            {/* Customize Button */}
                            {onCustomize && (
                                <button
                                    type="button"
                                    onClick={onCustomize}
                                    className="floating-admin-btn floating-admin-btn-customizer"
                                >
                                    <LuPalette size={13} style={{ flexShrink: 0 }} />
                                    <span>{typeof customizerLabel === 'string' ? customizerLabel.replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '').trim() : customizerLabel}</span>
                                </button>
                            )}

                            {/* Extra Buttons */}
                            {extraButtons}

                            {/* Dashboard Link */}
                            {dashboardUrl && (
                                <Link
                                    to={dashboardUrl}
                                    className="floating-admin-btn floating-admin-btn-ghost"
                                >
                                    <LuLayoutDashboard size={13} style={{ flexShrink: 0 }} />
                                    <span>Dashboard</span>
                                </Link>
                            )}
                        </div>

                        {/* Right Scroll Indicator / Quick Nudger */}
                        {canScrollRight && (
                            <button
                                type="button"
                                onClick={() => scrollByAmount(120)}
                                className="floating-admin-nav-arrow"
                                title="Scroll right"
                                aria-label="Scroll toolbar right"
                            >
                                <LuChevronRight size={13} style={{ flexShrink: 0 }} />
                            </button>
                        )}

                        {/* Position Selector (Left, Center, Right) */}
                        <div
                            className="floating-admin-pos-control"
                            title="Reposition toolbar: Left, Centre, or Right"
                        >
                            <button
                                type="button"
                                onClick={() => handlePositionChange('left')}
                                title="Dock toolbar to Left"
                                aria-label="Dock toolbar to Left"
                                className={`floating-admin-pos-btn ${barPosition === 'left' ? 'active' : ''}`}
                            >
                                <LuAlignLeft size={12} />
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePositionChange('center')}
                                title="Position toolbar in Centre (Default)"
                                aria-label="Position toolbar in Centre"
                                className={`floating-admin-pos-btn ${barPosition === 'center' ? 'active' : ''}`}
                            >
                                <LuAlignCenter size={12} />
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePositionChange('right')}
                                title="Dock toolbar to Right"
                                aria-label="Dock toolbar to Right"
                                className={`floating-admin-pos-btn ${barPosition === 'right' ? 'active' : ''}`}
                            >
                                <LuAlignRight size={12} />
                            </button>
                        </div>

                        {/* Minimize Button - Fixed on Right */}
                        <button
                            type="button"
                            onClick={() => handleToggleMinimize(true)}
                            className="floating-admin-min-btn"
                            title="Minimize editor bar"
                            aria-label="Minimize editor bar"
                        >
                            <LuChevronDown size={14} style={{ flexShrink: 0 }} />
                        </button>
                    </>
                )}
            </div>

            {/* CREATE NEW PAGE LIVE EDIT MODAL */}
            {isNewPageModalOpen && (
                <div
                    className="floating-admin-modal-overlay"
                    onClick={() => setIsNewPageModalOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15, 23, 42, 0.75)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        zIndex: 100000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px',
                        boxSizing: 'border-box'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#ffffff',
                            color: '#0f172a',
                            borderRadius: '20px',
                            width: '100%',
                            maxWidth: '520px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
                            overflow: 'hidden',
                            border: '1px solid #e2e8f0',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                            color: '#ffffff',
                            padding: '18px 24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    background: 'rgba(16, 185, 129, 0.2)',
                                    color: '#34d399',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <LuFilePlus size={18} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Create New Page</h3>
                                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>Create page & jump straight into Live Edit mode</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsNewPageModalOpen(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    color: '#94a3b8',
                                    border: 'none',
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <LuX size={16} />
                            </button>
                        </div>

                        {/* Modal Body / Form */}
                        <form onSubmit={handleCreatePageSubmit} style={{ padding: '24px' }}>
                            <div style={{ marginBottom: '18px' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                                    Page Title <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={newPageTitle}
                                    onChange={(e) => handleTitleChange(e.target.value)}
                                    placeholder="e.g. Services, About Us, Our Team, Pricing"
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '0.92rem',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'all 0.2s'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                                    URL Path / Slug
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '10px', overflow: 'hidden' }}>
                                    <span style={{ padding: '10px 12px', background: '#e2e8f0', color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>/</span>
                                    <input
                                        type="text"
                                        value={newPageSlug}
                                        onChange={(e) => {
                                            setIsSlugManuallyEdited(true);
                                            setNewPageSlug(e.target.value);
                                        }}
                                        placeholder="e.g. services"
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            border: 'none',
                                            background: 'transparent',
                                            fontSize: '0.9rem',
                                            color: '#0f172a',
                                            outline: 'none'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', margin: 0 }}>
                                        Starter Layout Template
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setIsMediaModalOpenForTemplate(true)}
                                            style={{
                                                background: '#f0fdf4',
                                                border: '1px solid #86efac',
                                                borderRadius: '6px',
                                                padding: '4px 10px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: '#166534',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <LuFolderOpen size={12} />
                                            <span>From Uploads</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            style={{
                                                background: '#f0f9ff',
                                                border: '1px solid #7dd3fc',
                                                borderRadius: '6px',
                                                padding: '4px 10px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: '#0284c7',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <LuUpload size={12} />
                                            <span>{customTemplateFileName ? 'Change JSON' : 'Upload JSON'}</span>
                                        </button>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".json"
                                        onChange={handleTemplateFileUpload}
                                        style={{ display: 'none' }}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                                    {[
                                        { id: 'blank', name: 'Blank Canvas', desc: 'Hero + Welcome Section', icon: '📄' },
                                        { id: 'services', name: 'Services & Features', desc: 'Hero + 3-Column Grid', icon: '💼' },
                                        { id: 'contact', name: 'Contact Form', desc: 'Hero + Interactive Form', icon: '📞' },
                                        { id: 'gallery', name: 'Media Showcase', desc: 'Hero + Photo Gallery', icon: '🖼️' }
                                    ].map((t) => (
                                        <div
                                            key={t.id}
                                            onClick={() => {
                                                setNewPageTemplate(t.id);
                                                setTemplateUploadError('');
                                            }}
                                            style={{
                                                border: `2px solid ${newPageTemplate === t.id ? '#10b981' : '#e2e8f0'}`,
                                                background: newPageTemplate === t.id ? '#ecfdf5' : '#ffffff',
                                                borderRadius: '12px',
                                                padding: '12px',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>{t.icon}</div>
                                            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>{t.name}</div>
                                            <div style={{ fontSize: '0.73rem', color: '#64748b' }}>{t.desc}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Custom Uploaded Template Card */}
                                {customTemplateFileName && (
                                    <div
                                        onClick={() => setNewPageTemplate('custom')}
                                        style={{
                                            marginTop: '10px',
                                            padding: '12px 14px',
                                            borderRadius: '12px',
                                            border: `2px solid ${newPageTemplate === 'custom' ? '#10b981' : '#38bdf8'}`,
                                            background: newPageTemplate === 'custom' ? '#ecfdf5' : '#f0f9ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#0284c7', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <LuFileCode size={18} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a' }}>
                                                    {customTemplateFileName}
                                                </div>
                                                <div style={{ fontSize: '0.73rem', color: '#0369a1' }}>
                                                    Custom Upload • {Array.isArray(customTemplateJson?.sections) ? customTemplateJson.sections.length : 0} section(s) loaded
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleClearCustomTemplate();
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: '#ef4444',
                                                cursor: 'pointer',
                                                padding: '4px'
                                            }}
                                            title="Remove custom template"
                                        >
                                            <LuTrash2 size={16} />
                                        </button>
                                    </div>
                                )}

                                {templateUploadError && (
                                    <div style={{ marginTop: '10px', padding: '10px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <LuCircleAlert size={15} style={{ flexShrink: 0 }} />
                                        <span>{templateUploadError}</span>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => setIsNewPageModalOpen(false)}
                                    style={{
                                        padding: '10px 18px',
                                        borderRadius: '10px',
                                        border: '1px solid #cbd5e1',
                                        background: '#ffffff',
                                        color: '#475569',
                                        fontSize: '0.88rem',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isCreatingPage}
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: '10px',
                                        border: 'none',
                                        background: '#10b981',
                                        color: '#ffffff',
                                        fontSize: '0.88rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)'
                                    }}
                                >
                                    <LuSparkles size={15} />
                                    <span>{isCreatingPage ? 'Creating...' : 'Create & Live Edit'}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <MediaLibraryModal
                isOpen={isMediaModalOpenForTemplate}
                onClose={() => setIsMediaModalOpenForTemplate(false)}
                onSelect={handleSelectTemplateFromUploads}
                title="Select Page Template from Uploads"
                allowedTypes={['template', 'document']}
            />
        </>
    );
};

export default FloatingAdminBar;

